// @ts-nocheck - Temporary fix for backend integration types
import { httpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';
import type { User } from '@/types';

export class BackendUserService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  private getUserId(): string | undefined {
    return localStorage.getItem('user_id') || undefined;
  }

  // User Profile Management
  async getUserProfile(userId?: string): Promise<User> {
    const targetUserId = userId || this.getUserId();
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${targetUserId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user profile');
    }

    return this.mapUserFromBackend(response.data);
  }

  async updateUserProfile(userId: string, updates: Partial<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      county: string;
      postalCode: string;
    };
    preferences: any;
    avatar: string;
  }>): Promise<User> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.USERS}/${userId}`, updates, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update user profile');
    }

    return this.mapUserFromBackend(response.data);
  }

  async deleteUserAccount(userId: string): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.USERS}/${userId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete user account');
    }
  }

  // User Verification
  async verifyEmail(token: string): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/verify-email`, {
      token
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to verify email');
    }
  }

  async resendVerificationEmail(email: string): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/resend-verification`, {
      email
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to resend verification email');
    }
  }

  async verifyPhone(userId: string, code: string): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/verify-phone`, {
      code
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to verify phone number');
    }
  }

  // User Preferences
  async getUserPreferences(userId: string): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/preferences`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user preferences');
    }

    return response.data;
  }

  async updateUserPreferences(userId: string, preferences: any): Promise<void> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/preferences`, preferences, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update user preferences');
    }
  }

  // User Activity
  async getUserActivity(userId: string, page = 1, limit = 20): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/activity`, {
      params: { page: page.toString(), limit: limit.toString() },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user activity');
    }

    return response.data;
  }

  // User Jobs
  async getUserJobs(userId: string, status?: string, page = 1, limit = 20): Promise<any> {
    const params: Record<string, string> = { 
      page: page.toString(), 
      limit: limit.toString() 
    };
    if (status) params.status = status;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/jobs`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user jobs');
    }

    return response.data;
  }

  // User Reviews
  async getUserReviews(userId: string, type: 'given' | 'received' = 'received', page = 1, limit = 10): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/reviews`, {
      params: { 
        type, 
        page: page.toString(), 
        limit: limit.toString() 
      },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user reviews');
    }

    return response.data;
  }

  async createReview(reviewData: {
    jobId: string;
    providerId: string;
    rating: number;
    comment: string;
    categories?: Record<string, number>;
  }): Promise<any> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/reviews`, reviewData, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create review');
    }

    return response.data;
  }

  // User Favorites
  async getFavoriteProviders(userId: string): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/favorites`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch favorite providers');
    }

    return response.data || [];
  }

  async addFavoriteProvider(userId: string, providerId: string): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/favorites`, {
      provider_id: providerId
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to add favorite provider');
    }
  }

  async removeFavoriteProvider(userId: string, providerId: string): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/favorites/${providerId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to remove favorite provider');
    }
  }

  // User Addresses
  async getUserAddresses(userId: string): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/addresses`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user addresses');
    }

    return response.data || [];
  }

  async addUserAddress(userId: string, address: {
    label: string;
    street: string;
    city: string;
    county: string;
    postalCode: string;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/addresses`, address, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to add user address');
    }

    return response.data;
  }

  async updateUserAddress(userId: string, addressId: string, updates: any): Promise<any> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/addresses/${addressId}`, updates, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update user address');
    }

    return response.data;
  }

  async deleteUserAddress(userId: string, addressId: string): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/addresses/${addressId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete user address');
    }
  }

  // User Payment Methods
  async getPaymentMethods(userId: string): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/payment-methods`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch payment methods');
    }

    return response.data || [];
  }

  async addPaymentMethod(userId: string, paymentMethod: {
    type: 'card' | 'bank_account';
    token: string;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/payment-methods`, paymentMethod, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to add payment method');
    }

    return response.data;
  }

  async deletePaymentMethod(userId: string, paymentMethodId: string): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/payment-methods/${paymentMethodId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete payment method');
    }
  }

  // User Statistics
  async getUserStats(userId: string): Promise<{
    totalJobs: number;
    completedJobs: number;
    totalSpent: number;
    averageRating: number;
    reviewCount: number;
    memberSince: string;
  }> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/stats`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user statistics');
    }

    return response.data;
  }

  // Helper method to map backend user data to frontend User type
  private mapUserFromBackend(data: any): User {
    return {
      id: data.id,
      email: data.email,
      firstName: data.first_name || data.firstName,
      lastName: data.last_name || data.lastName,
      phone: data.phone,
      avatar: data.avatar || data.profile_image,
      role: data.role || 'customer',
      isVerified: data.is_verified || data.isVerified || false,
      emailVerified: data.email_verified || false,
      phoneVerified: data.phone_verified || false,
      address: data.address ? {
        street: data.address.street,
        city: data.address.city,
        county: data.address.county,
        postalCode: data.address.postal_code || data.address.postalCode
      } : undefined,
      preferences: data.preferences || {},
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }
}

export const backendUserService = new BackendUserService();